import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { keyframes, styled } from '@storybook/theming';
import { Icon, styles } from '@storybook/design-system';

const { color, typography } = styles;

// Ghost image cards and gallery images. Images the author already linked
// somewhere are skipped so we don't hijack the link, and GIFs are skipped
// because they're usually short animations that aren't worth enlarging.
const IMAGE_SELECTOR = '.kg-image-card img, .kg-gallery-image img';
export const TRIGGER_CLASS = 'lightbox-trigger';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
`;

const Dialog = styled.dialog`
  /* Fill the viewport so clicks outside the image land on the dialog itself */
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${color.lightest};

  &::backdrop {
    background: rgba(0, 0, 0, 0.88);
  }

  @media (prefers-reduced-motion: no-preference) {
    &[open],
    &[open]::backdrop {
      animation: ${fadeIn} 150ms ease-out;
    }
  }
`;

const Figure = styled.figure`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  margin: 0;
  padding: 56px 20px 20px;
  cursor: zoom-out;

  img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    min-height: 0;
    object-fit: contain;
    background: ${color.lightest};
    cursor: default;
  }

  figcaption {
    margin-top: 12px;
    max-width: 65ch;
    font-size: ${typography.size.s2}px;
    line-height: 1.5;
    text-align: center;
    cursor: default;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: ${color.lightest};
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.24);
  }

  &:focus-visible {
    outline: 2px solid ${color.lightest};
    outline-offset: 2px;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const isGif = (img) =>
  /\.gif$/i.test((img.getAttribute('src') || '').split(/[?#]/)[0]);

const getCaption = (img) => {
  const figure = img.closest('figure');
  const caption = figure && figure.querySelector('figcaption');
  return caption ? caption.textContent.trim() : '';
};

/**
 * Makes images inside Ghost-rendered HTML open in an accessible lightbox.
 *
 * Each eligible image is wrapped in a real <button> so it is keyboard
 * focusable and announced as interactive. The viewer is a native modal
 * <dialog>, which gives us focus trapping, Escape-to-close and an inert
 * page behind it for free.
 */
export const ImageLightbox = ({ containerRef, html }) => {
  const dialogRef = useRef(null);
  const triggerRef = useRef(null);
  const [image, setImage] = useState(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const triggers = [];

    container.querySelectorAll(IMAGE_SELECTOR).forEach((img) => {
      if (img.closest('a, button') || isGif(img)) return;

      const alt = img.getAttribute('alt') || '';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = TRIGGER_CLASS;
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute(
        'aria-label',
        alt ? `Enlarge image: ${alt}` : 'Enlarge image'
      );
      img.replaceWith(button);
      button.appendChild(img);
      triggers.push(button);
    });

    const handleClick = (event) => {
      const button = event.target.closest(`.${TRIGGER_CLASS}`);
      if (!button || !container.contains(button)) return;
      const img = button.querySelector('img');
      if (!img) return;

      triggerRef.current = button;
      setImage({
        // Ghost puts the original upload in src and smaller sizes in srcset
        src: img.getAttribute('src'),
        alt: img.getAttribute('alt') || '',
        caption: getCaption(img),
      });
    };

    container.addEventListener('click', handleClick);

    return () => {
      container.removeEventListener('click', handleClick);
      triggers.forEach((button) => {
        const img = button.querySelector('img');
        if (img && button.parentNode) button.replaceWith(img);
      });
    };
  }, [containerRef, html]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (image && dialog && !dialog.open) {
      dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
    }
  }, [image]);

  // Restore page scrolling if we unmount while open (e.g. route change)
  useEffect(
    () => () => {
      document.documentElement.style.overflow = '';
    },
    []
  );

  const close = useCallback(() => {
    const dialog = dialogRef.current;
    if (dialog && dialog.open) dialog.close();
  }, []);

  // Fires for every close path: Escape, close button, backdrop click
  const handleClose = useCallback(() => {
    document.documentElement.style.overflow = '';
    setImage(null);
    if (triggerRef.current) triggerRef.current.focus();
  }, []);

  const handleBackdropClick = (event) => {
    // Close when clicking anywhere except the image or caption
    if (
      event.target.tagName !== 'IMG' &&
      event.target.tagName !== 'FIGCAPTION'
    ) {
      close();
    }
  };

  return (
    <Dialog
      ref={dialogRef}
      aria-label="Enlarged image"
      onClose={handleClose}
      onClick={handleBackdropClick}
    >
      <CloseButton type="button" onClick={close} aria-label="Close">
        <Icon icon="cross" aria-hidden />
      </CloseButton>
      {image && (
        <Figure>
          <img src={image.src} alt={image.alt} />
          {image.caption && <figcaption>{image.caption}</figcaption>}
        </Figure>
      )}
    </Dialog>
  );
};

ImageLightbox.propTypes = {
  containerRef: PropTypes.shape({ current: PropTypes.instanceOf(Object) })
    .isRequired,
  html: PropTypes.string.isRequired,
};
